import axios from "axios";
import router from "@/router";

const getDefaultState = () => {
  return {
    allOrders: [],
    allOrdersLoading: false,
    fields: {
      ALL_ORDERS: `
        id
        order_no
        created_at
        status
        total_price_with_vat
        currency
        agent {
          full_name
        }
        customer {
          id
          company_name
          mollie_order_static
        }
        number_of_devices
        payment_type
        contract_start_on
        first_payment_value
        cancell_at
        notes
        send_invoices
        _rowVariant
      `
    }
  };
};

const state = getDefaultState();

const getters = {
  getAllOrders(state) {
    return state.allOrders;
  },
  getAllOrdersLoading(state) {
    return state.allOrdersLoading;
  }
};

const actions = {
  fetchAllOrders({ commit, dispatch, state }) {
    commit("setAllOrdersLoading", true);

    const data = {
      query: `
        query {
          allOrders(limit: 20) { ${state.fields.ALL_ORDERS} }
        }
      `
    };
    const data_1 = {
      query: `
        query {
          allOrders(offset: 20) { ${state.fields.ALL_ORDERS} }
        }
      `
    };

    dispatch("fetchOrders", { data: data, commit_to: "setAllOrders" }).then(
      () => {
        dispatch("fetchOrders", {
          data: data_1,
          commit_to: "setAddToAllOrders"
        }).then(() => {
          commit("setAllOrdersLoading", false);
        });
      }
    );
  },
  fetchOrders({ commit, rootGetters }, payload) {
    return new Promise((resolve, reject) => {
      axios
        .post(rootGetters.getGraphQlUrl, payload.data, rootGetters.getDefConf)
        .then(res => {
          if (res.data.errors !== undefined && res.data.errors.length > 0) {
            alert(res.data.errors[0].message);
          } else {
            commit(payload.commit_to, res.data.data.allOrders);
          }
        })
        .catch(err => {
          alert(err);
          console.error(err);
        })
        .then(() => {
          resolve();
        });
    });
  },
  saveNewOrder({ commit, rootGetters }, payload) {
    commit("setShowLoadingModal", true, { root: true });
    let data = {
      query: `
        mutation OrderCreate($order: OrderInput) {
          OrderCreate (order: $order) { id }
        }
      `,
      variables: {
        order: payload.newOrderData
      }
    };

    axios
      .post(rootGetters.getGraphQlUrl, data, rootGetters.getDefConf)
      .then(res => {
        if (res.data.errors !== undefined && res.data.errors.length > 0) {
          alert(res.data.errors[0].message);
        } else {
          router.push({
            name: "order",
            params: {
              id: parseInt(res.data.data.OrderCreate.id)
            }
          });
        }
      })
      .catch(err => {
        alert(err);
        console.error(err);
      })
      .then(() => {
        commit("setShowLoadingModal", false, { root: true });
      });
  },
  fetchOrdersPagination({ state, dispatch }, payload) {
    const commitPayload = {
      this_ref: payload.this_ref,
      data: {
        query: `
          query {
            orders: OrdersPagination (
              ${payload.status != null ? `status: "${payload.status}",` : ''}
              limit: ${payload.limit},
              offset: ${payload.offset},
              order: "${payload.order}"
            ) { ${state.fields.ALL_ORDERS} }
          }
        `
      }
    };

    dispatch("fetchCommitOrders", commitPayload);
  },
  fetchOrderOrderNo({ state, dispatch }, payload) {
    const commitPayload = {
      this_ref: payload.this_ref,
      data: {
        query: `
          query {
            orders: OrdersByOrderNo(
              order_no: "${payload.orderNo}"
            ) {
              ${state.fields.ALL_ORDERS}
            }
          }
        `
      }
    };

    dispatch("fetchCommitOrders", commitPayload);
  },
  fetchOrdersLikeAgent({ state, dispatch }, payload) {
    const commitPayload = {
      this_ref: payload.this_ref,
      data: {
        query: `
          query {
            orders: OrdersLikeAgentName(
              agent_name: "${payload.agentName}",
              ${payload.status != null ? `status: "${payload.status}",` : ''}
              limit: ${payload.limit},
              offset: ${payload.offset},
            ) {
              ${state.fields.ALL_ORDERS}
            }
          }
        `
      }
    };

    dispatch("fetchCommitOrders", commitPayload);
  },
  fetchOrdersLikeCustomer({ state, dispatch }, payload) {
    // Build query parameters
    const params = [`customer_name: "${payload.customerName}"`];
    if (payload.status != null) {
      params.push(`status: "${payload.status}"`);
    }
    params.push(`limit: ${payload.limit}`);
    params.push(`offset: ${payload.offset}`);

    const commitPayload = {
      this_ref: payload.this_ref,
      data: {
        query: `
          query {
            orders: OrdersLikeCustomerName(
              ${params.join(', ')}
            ) {
              ${state.fields.ALL_ORDERS}
            }
          }
        `
      }
    };

    dispatch("fetchCommitOrders", commitPayload);
  },
  fetchCommitOrders({ state, commit, rootGetters, dispatch }, payload) {
    return new Promise((resolve) => {
      commit("setAllOrdersLoading", true);

      axios
        .post(rootGetters.getGraphQlUrl, payload.data, rootGetters.getDefConf)
        .then(res => {
          if (res.data.errors !== undefined && res.data.errors.length > 0) {
            res.data.errors.forEach(e => {
              // Check for timeout error to logout
              if (e.message == "Login timeout, logout and login again.") {
                dispatch("auth/logOut", null, { root: true });
                resolve(false);
              }

              dispatch("axios_mod/makeErrorToast", {
                this_ref: payload.this_ref,
                content: e.message != null ? e.message : "Something went wrong."
              }, { root: true });
            });
            resolve(false);
          } else {
            commit('setAllOrders', res.data.data.orders);
            resolve();
          }
        })
        .catch(err => {
          dispatch("axios_mod/makeErrorToast", {
            this_ref: payload.this_ref,
            content: err != null ? err : "Something went wrong."
          }, { root: true });
          resolve(false);
        })
        .finally(() => {
          commit("setAllOrdersLoading", false);
        });
    });
  },
};

const mutations = {
  setAllOrders(state, value) {
    state.allOrders = value || [];
  },
  setAddToAllOrders(state, value) {
    state.allOrders.push(...value);
  },
  setAllOrdersLoading(state, value) {
    state.allOrdersLoading = value;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
